/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Injectable } from "@angular/core";

/**
 * Timezone Helper Service
 * Based on old QMC DateHelper and timezone logic
 * Handles complex timezone conversions between site timezone and customer timezone
 */
@Injectable({
  providedIn: "root",
})
export class TimezoneHelper {
  /**
   * Format UTC offset for display (e.g., "+5", "-7")
   * Based on old QMC getUTC() method
   */
  formatUtcOffset(utcOffset: number | null | undefined): string {
    if (utcOffset == null) return "+0";
    return utcOffset >= 0 ? `+${utcOffset}` : utcOffset.toString();
  }

  /**
   * Format GMT offset for display with label (e.g., "(GMT+5)", "(UTC-7)")
   * Based on old QMC getFormattedGMT() method
   */
  formatGmtOffset(
    utcOffset: number | null | undefined,
    timeFormat: string = "GMT",
  ): string {
    const offset = utcOffset == null ? 0 : utcOffset;
    const sign = offset < 0 ? "" : "+"; // Negative already has the sign
    return `(${timeFormat}${sign}${offset})`;
  }

  /**
   * Convert date to specific timezone for display
   * Takes UTC date and applies timezone offset
   * Based on old QMC getDateFromUtcOffsetForDisplay() method
   *
   * IMPORTANT: JavaScript Date objects automatically display in the browser's local timezone.
   * To display in a different timezone, we need to compensate for the browser's timezone first.
   */
  convertToTimezone(
    date: Date | string | null | undefined,
    utcOffset: number,
  ): Date | null {
    if (!date) return null;

    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;

    // Compensate for browser's local timezone (matches old QMC logic)
    // getTimezoneOffset() returns minutes, convert to hours
    const browserOffsetHours = new Date().getTimezoneOffset() / 60;
    const adjustedOffset = utcOffset + browserOffsetHours;

    // Apply adjusted timezone offset
    const offsetMs = adjustedOffset * 60 * 60 * 1000;
    return new Date(dateObj.getTime() + offsetMs);
  }

  /**
   * Format date with timezone consideration
   * Based on old QMC dateFormat pipe logic
   */
  formatDateWithTimezone(
    date: Date | string | null | undefined,
    utcOffset: number,
    format: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  ): string {
    if (!date) return "-";

    const convertedDate = this.convertToTimezone(date, utcOffset);
    if (!convertedDate) return "-";

    return convertedDate.toLocaleDateString("en-US", format);
  }

  /**
   * Create comprehensive timezone tooltip
   * Based on old QMC complex tooltip logic showing both site and customer timezones
   */
  createTimezoneTooltip(
    date: Date | string | null | undefined,
    siteUtcOffset: number,
    customerUtcOffset: number,
    fieldName: string = "Date",
  ): string {
    if (!date) return `No ${fieldName.toLowerCase()} available`;

    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "Invalid date";

    const siteDate = this.convertToTimezone(dateObj, siteUtcOffset);
    const customerDate = this.convertToTimezone(dateObj, customerUtcOffset);

    if (!siteDate || !customerDate) return "Invalid date";

    const dateFormat: Intl.DateTimeFormatOptions = {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };

    return [
      `<span class="font-semibold">Site Timezone:</span> ${this.formatGmtOffset(siteUtcOffset, "UTC")}`,
      `<span class="font-semibold">${fieldName} (Site):</span> ${siteDate.toLocaleDateString("en-US", dateFormat)} ${this.formatGmtOffset(siteUtcOffset, "UTC")}`,
      `<span class="font-semibold">Customer Timezone:</span> ${this.formatGmtOffset(customerUtcOffset, "UTC")}`,
      `<span class="font-semibold">${fieldName} (Customer):</span> ${customerDate.toLocaleDateString("en-US", dateFormat)} ${this.formatGmtOffset(customerUtcOffset, "UTC")}`,
    ].join("\n");
  }

  /**
   * Get date for filtering with timezone conversion
   * Based on old QMC getDateFromUtcOffsetForFiltering() method
   */
  getDateForFiltering(
    date: Date,
    utcOffset: number,
    isEndOfDay: boolean = false,
  ): Date {
    const offsetMs = utcOffset * 60 * 60 * 1000;
    const convertedDate = new Date(date.getTime() + offsetMs);

    if (isEndOfDay) {
      // Set to end of day (23:59:59.999)
      convertedDate.setHours(23, 59, 59, 999);
    } else {
      // Set to start of day (00:00:00.000)
      convertedDate.setHours(0, 0, 0, 0);
    }

    return convertedDate;
  }

  /**
   * Check if two timezone offsets are different enough to show both
   */
  shouldShowDualTimezone(
    siteUtcOffset: number,
    customerUtcOffset: number,
  ): boolean {
    return Math.abs(siteUtcOffset - customerUtcOffset) > 0;
  }

  /**
   * Get timezone display name from UTC offset
   */
  getTimezoneDisplayName(utcOffset: number): string {
    // Common timezone mappings (simplified)
    const timezoneMap: Record<string, string> = {
      "-8": "PST",
      "-7": "MST/PDT",
      "-6": "CST/MDT",
      "-5": "EST/CDT",
      "-4": "EDT",
      "0": "GMT/UTC",
      "1": "CET",
      "2": "EET",
      "8": "CST (China)",
      "9": "JST",
    };

    const key = utcOffset.toString();
    return (
      (timezoneMap[key] as string) ?? `UTC${this.formatUtcOffset(utcOffset)}`
    );
  }

  /**
   * Format relative time (e.g., "2 hours ago", "in 3 days")
   * Enhanced version of old QMC time display logic
   */
  formatRelativeTime(date: Date | string | null | undefined): string {
    if (!date) return "-";

    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "Invalid date";

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMs > 0) {
      // Past
      if (diffMinutes < 1) return "Just now";
      if (diffMinutes < 60)
        return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
      if (diffDays < 30)
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } else {
      // Future
      const absDiffMinutes = Math.abs(diffMinutes);
      const absDiffHours = Math.abs(diffHours);
      const absDiffDays = Math.abs(diffDays);

      if (absDiffMinutes < 60)
        return `in ${absDiffMinutes} minute${absDiffMinutes !== 1 ? "s" : ""}`;
      if (absDiffHours < 24)
        return `in ${absDiffHours} hour${absDiffHours !== 1 ? "s" : ""}`;
      if (absDiffDays < 30)
        return `in ${absDiffDays} day${absDiffDays !== 1 ? "s" : ""}`;
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }

  /**
   * Check if date is within processing time window
   * Based on old QMC IsInRepeatTimeWindow logic
   */
  isWithinProcessingTimeWindow(
    lastProcessedDate: Date | string | null | undefined,
    windowHours: number = 24,
  ): boolean {
    if (!lastProcessedDate) return false;

    const dateObj =
      typeof lastProcessedDate === "string"
        ? new Date(lastProcessedDate)
        : lastProcessedDate;
    if (isNaN(dateObj.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours < windowHours;
  }

  /**
   * Convert local date to UTC for API calls
   */
  convertLocalToUtc(date: Date, utcOffset: number): Date {
    const offsetMs = utcOffset * 60 * 60 * 1000;
    return new Date(date.getTime() - offsetMs);
  }

  /**
   * Get current date in specific timezone
   */
  getCurrentDateInTimezone(utcOffset: number): Date {
    const now = new Date();
    return this.convertToTimezone(now, utcOffset) || now;
  }

  /**
   * Get the UTC date value using a specified timezone offset and time
   * Matches old QMC DateHelper.getUtcDateFromTimezoneOffset() method (lines 86-106)
   * Used for Nextime next occurrence alignment in V3 sites with processing windows
   *
   * @param date Date object representing the next occurrence date
   * @param time Time string in format "HH:MM" (e.g., "14:30")
   * @param offset UTC offset in hours (e.g., -5 for EST, +8 for CST)
   * @returns ISO string of the date in UTC, or undefined if inputs invalid
   */
  getUtcDateFromTimezoneOffset(
    date: Date,
    time: string,
    offset: number,
  ): string | undefined {
    if (!date || !time) {
      return undefined;
    }

    // Pad offset to 2 digits and format as +HH:00 or -HH:00
    const paddedOffset = Math.abs(offset).toString().padStart(2, "0");
    const offsetString =
      offset >= 0 ? `+${paddedOffset}:00` : `-${paddedOffset}:00`;

    // Extract date components
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    // Extract time components
    const timeParts = time.split(":");
    const hour = parseInt(timeParts[0] || "0")
      .toString()
      .padStart(2, "0");
    const minute = parseInt(timeParts[1] || "0")
      .toString()
      .padStart(2, "0");

    // Build ISO string with timezone offset (e.g., "2024-10-22T14:30:00.000+08:00")
    const isoString = `${year}-${month}-${day}T${hour}:${minute}:00.000${offsetString}`;

    // Parse and return as JSON (UTC)
    const timestamp = Date.parse(isoString);
    if (isNaN(timestamp)) {
      return undefined;
    }

    const nextOccurrence = new Date(timestamp);
    return nextOccurrence.toJSON();
  }

  /**
   * Convert date from UTC offset for saving to API
   * Matches old QMC DateHelper.getDateFromUtcOffsetForSave() method (lines 71-81)
   * When saving a date, the UTC offset must be subtracted from the selected date
   * Also compensates for the local browser timezone
   *
   * @param date Date object to convert
   * @param offset UTC offset in hours for the target timezone
   * @returns Date adjusted for API save
   */
  getDateFromUtcOffsetForSave(date: Date, offset: number): Date {
    // Compensate for browser's local timezone (in hours)
    const browserOffsetHours = new Date().getTimezoneOffset() / 60;
    const adjustedOffset = offset + browserOffsetHours;

    // Convert offset to milliseconds and subtract from date
    const offsetMs = adjustedOffset * 60 * 60 * 1000;
    const newDate = new Date(date.getTime() - offsetMs);

    return newDate;
  }
}
